import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { LOCALES, type Locale } from '@/i18n';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useLocale } from '@/i18n/LocaleContext';
import { cn } from '@/lib/cn';

type Props = {
  compact?: boolean; // header versão pequena
  className?: string;
};

const FLAGS: Record<Locale, string> = {
  'pt-BR': '🇧🇷',
  'en-US': '🇺🇸',
  es: '🇪🇸',
};

export function LanguageSwitcher({ compact = true, className }: Props) {
  const { t } = useTranslation('common');
  const { switchLocale } = useLocalizedNavigate();
  // No app a URL não carrega idioma: o atual é o do contexto (URL no site, escolha no app).
  const currentLocale = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t('language.label')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-vyxora-text-light/80 transition hover:border-vyxora-cyan-400/40 hover:text-vyxora-text-light',
          compact ? 'text-xs' : 'text-sm',
        )}
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">{FLAGS[currentLocale]}</span>
        <span className="font-semibold uppercase tracking-wider">
          {t(`language.short${normalize(currentLocale)}`)}
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-lg border border-vyxora-cyan-400/20 bg-vyxora-panel shadow-glow"
        >
          {LOCALES.map((loc) => (
            <li key={loc}>
              <button
                onClick={() => {
                  switchLocale(loc);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-sm text-vyxora-text-light/80 transition hover:bg-vyxora-cyan-400/10',
                  loc === currentLocale && 'bg-vyxora-cyan-400/10 text-vyxora-text-light',
                )}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden>{FLAGS[loc]}</span>
                  <span>{t(`language.${loc}`)}</span>
                </span>
                {loc === currentLocale && <Check className="h-4 w-4 text-vyxora-cyan-300" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function normalize(loc: Locale): string {
  // "pt-BR" → "Pt-BR" (para casar com chaves shortPt-BR / shortEn-US / shortEs)
  return loc.charAt(0).toUpperCase() + loc.slice(1);
}
